/**
 * Tour engine helpers — pure functions, no React, no DOM.
 *
 * `useTour` drives the clock; everything that can be reasoned about without a
 * browser lives here so `test/studyninja-living-tour.test.mjs` can cover it:
 * resolving a scene's dwell and beats from the script (with the fallback dwell
 * when a scene is missing), advancing beats by `at`, the next-scene rule (the
 * finale never loops), the coarse progress steps published to React, the
 * per-session nudge bookkeeping and the act labels shown by the tour bar.
 */

/* Explicit extensions: this module also runs under `node --test --experimental-strip-types`. */
import { REFERENCE_SCENES, type ReferenceSceneId } from '../../reference/types.ts';
import type { Bilingual, Persona, TourBeat, TourScript } from '../types.ts';

/** Dwell for a scene the script does not cover. */
export const FALLBACK_DWELL_MS = 9000;
/** Idle time on a scene before the proactive nudge appears. */
export const NUDGE_AFTER_MS = 7000;
/** sessionStorage key: comma-separated `scene:persona` keys already nudged this session. */
export const NUDGE_STORAGE_KEY = 'sn-nudged';
/** CSS custom property written on the `.experience` root every frame while touring. */
export const TOUR_PROGRESS_VAR = '--sn-tour-p';
/**
 * React only learns about progress in these steps (plus every beat change);
 * the smooth value travels through the CSS variable, never through state.
 */
export const PROGRESS_STEPS = 20;
/** How long the loop waits for `navigate()` to land the next scene before it gives up and pauses. */
export const NAV_WAIT_MS = 2500;

export interface ResolvedTourScene {
  duration: number;
  beats: readonly TourBeat[];
}

const EMPTY_BEATS: readonly TourBeat[] = [];

/** The script entry for a scene, or the fallback dwell with no beats. */
export function sceneEntry(script: TourScript, scene: ReferenceSceneId): ResolvedTourScene {
  const entry = script.find((item) => item.scene === scene);
  if (!entry) return { duration: FALLBACK_DWELL_MS, beats: EMPTY_BEATS };
  const duration = Number.isFinite(entry.duration) && entry.duration > 0 ? entry.duration : FALLBACK_DWELL_MS;
  return { duration, beats: entry.beats };
}

/**
 * Index of the last beat whose `at` is ≤ `elapsed`, scanning forward from
 * `from` (the beat already active). -1 before the first beat. Beats are
 * sorted ascending (asserted by the content tests), so a long frame or a
 * resume simply walks through every beat it crossed.
 */
export function beatIndexAt(beats: readonly TourBeat[], elapsed: number, from = -1): number {
  let index = from < -1 ? -1 : from;
  while (index + 1 < beats.length && beats[index + 1].at <= elapsed) index += 1;
  return index;
}

/** The scene the tour moves to after `scene`; null after the last one (the tour never loops). */
export function nextScene(scene: ReferenceSceneId): ReferenceSceneId | null {
  const index = REFERENCE_SCENES.indexOf(scene);
  if (index < 0 || index >= REFERENCE_SCENES.length - 1) return null;
  return REFERENCE_SCENES[index + 1];
}

/** Coarse bucket of a 0..1 progress value (see PROGRESS_STEPS). */
export function progressStep(progress: number): number {
  if (!(progress > 0)) return 0;
  if (progress >= 1) return PROGRESS_STEPS;
  return Math.floor(progress * PROGRESS_STEPS);
}

/** Beat offsets as 0..1 fractions of the scene dwell, for the tour bar ticks (edges excluded). */
export function beatTicks(script: TourScript, scene: ReferenceSceneId): number[] {
  const { duration, beats } = sceneEntry(script, scene);
  return beats
    .map((beat) => beat.at / duration)
    .filter((fraction) => fraction > 0 && fraction < 1);
}

/* ── nudge bookkeeping (once per scene × persona per session) ────────────── */

export function nudgeKey(scene: ReferenceSceneId, persona: Persona): string {
  return `${scene}:${persona}`;
}

export function parseNudged(raw: string | null | undefined): Set<string> {
  const keys = new Set<string>();
  if (!raw) return keys;
  for (const part of raw.split(',')) {
    const key = part.trim();
    if (key) keys.add(key);
  }
  return keys;
}

export function serializeNudged(keys: ReadonlySet<string>): string {
  return Array.from(keys).join(',');
}

/* ── acts (labels flashed by the tour bar when an act begins) ─────────────── */

export interface TourAct {
  /** Index of the first scene of the act. */
  start: number;
  label: Bilingual;
}

export const TOUR_ACTS: readonly TourAct[] = [
  { start: 0, label: { ru: 'I · Увидеть', en: 'I. See' } },
  { start: 3, label: { ru: 'II · Связать', en: 'II. Connect' } },
  { start: 6, label: { ru: 'III · Почувствовать и решить', en: 'III. Feel and decide' } },
  { start: 8, label: { ru: 'IV · Честно и дальше', en: 'IV. Honest and onward' } },
];

/** The act label to flash when the tour lands on `sceneIndex`, or null when no act starts there. */
export function actStartingAt(sceneIndex: number): Bilingual | null {
  const act = TOUR_ACTS.find((item) => item.start === sceneIndex);
  return act ? act.label : null;
}
